import React, { useCallback } from 'react';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n';
import Platform from 'platform';
import { LIST_ITEMS_SIZE } from 'config';
import Clock from 'components/Clock';
import WithMouseControls from 'components/HOCs/WithMouseControls';
import withBackSupport from 'components/HOCs/withBackSupport';
import { goToLoginPage } from 'store/app/actions';
import useModals from 'hooks/useModals';
import {
  selectIsLoggedIn,
  selectProfileItems,
  selectIsParentalConterolEnabled,
} from 'store/profile/selectors';
import ProfileItem from './ProfileItem';
import {
  StyledProfileTitle,
  StyledProfileList,
  StyledProfileVersion,
  StyledProfileSupportInfo,
} from './ProfileStyledComponents';

const SECTION_NAME = 'PROFILE_ITEMS_LIST';

const LIST_CONFIG = { enterTo: 'last-focused' };

const ProfilePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showLogoutModal, showLoginModal, showPinModal } = useModals();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isParentalEnabled = useSelector(selectIsParentalConterolEnabled);
  const items = useSelector(selectProfileItems);
  const appVersion = Platform.getAppVersion();

  const onProfileItemEnter = useCallback(
    (item) => {
      switch (item.id) {
        case 'authorization':
          if (isLoggedIn) {
            showLogoutModal({ sectionToFocus: SECTION_NAME });
          } else {
            dispatch(goToLoginPage());
          }
          break;
        case 'parental-control':
          if (!isLoggedIn) {
            showLoginModal({ sectionToFocus: SECTION_NAME });
            return;
          }
          if (isParentalEnabled) {
            const onModalConfirm = () => history.push('parental-control');
            showPinModal({ sectionToFocus: `@${SECTION_NAME}` }, onModalConfirm);
          } else {
            history.push('parental-control');
          }
          break;
      }
    },
    [
      dispatch,
      history,
      isLoggedIn,
      isParentalEnabled,
      showLoginModal,
      showLogoutModal,
      showPinModal,
    ],
  );

  return (
    <>
      <Clock />
      <StyledProfileTitle>Profile</StyledProfileTitle>
      <StyledProfileList
        sectionName={SECTION_NAME}
        listId="profile-list"
        config={LIST_CONFIG}
        onEnter={onProfileItemEnter}
        itemComponent={ProfileItem}
        startScrollIndex={1}
        items={items}
        itemSize={LIST_ITEMS_SIZE.MEDIUM.width}
        defaultSection
      />
      <StyledProfileSupportInfo>
        <div>{i18n.t('profile.supportInfo')}</div>
        <span>support@mometu.com</span>
      </StyledProfileSupportInfo>
      <StyledProfileVersion>
        <div>{i18n.t('profile.version')}</div>
        <span>{appVersion}</span>
      </StyledProfileVersion>
    </>
  );
};

export default compose<React.FC>(WithMouseControls, withBackSupport)(ProfilePage);
